import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageHeader from "@/components/base/PageHeader";
import EmptyState from "@/components/base/EmptyState";
import EpisodeCard from "@/components/feature/EpisodeCard";
import CharacterCard from "@/components/feature/CharacterCard";
import { formatDate, searchAll } from "@/utils/content";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") || "";
  const [input, setInput] = useState(query);

  const result = useMemo(() => searchAll(query), [query]);

  const submit = () => {
    const q = input.trim();
    if (!q) {
      setParams({});
      return;
    }
    setParams({ q });
  };

  return (
    <div>
      <PageHeader
        eyebrow="搜索"
        icon="ri-search-line"
        title={query ? `「${query}」的搜索结果` : "全站搜索"}
        description="搜索覆盖剧集标题、角色名与资讯标题，输入关键词即可查看匹配内容。"
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3">
            <i className="ri-search-line text-foreground-500" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
              }}
              placeholder="搜索剧集、角色或资讯"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
          <button
            type="button"
            onClick={submit}
            className="rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </div>

        {!query ? (
          <div className="mt-8">
            <EmptyState
              title="输入关键词开始搜索"
              description="你可以搜索剧集标题、角色名或资讯标题，例如「鸣人」「中忍考试」「写轮眼」。"
            />
          </div>
        ) : result.total === 0 ? (
          <div className="mt-8">
            <EmptyState
              title={`没有找到与「${query}」相关的内容`}
              description="试试更换关键词，或前往剧集、角色、资讯页面浏览。"
            />
          </div>
        ) : (
          <div className="mt-8 space-y-12">
            <p className="text-sm text-foreground-600">
              共找到 <strong className="text-foreground-950">{result.total}</strong> 条结果：
              剧集 {result.episodes.length} · 角色 {result.characters.length} · 资讯 {result.news.length}
            </p>

            {result.episodes.length > 0 && (
              <section>
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-film-line text-primary-600" />
                  相关剧集
                </h2>
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {result.episodes.map((episode) => (
                    <EpisodeCard key={episode.id} episode={episode} />
                  ))}
                </div>
              </section>
            )}

            {result.characters.length > 0 && (
              <section>
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-user-star-line text-primary-600" />
                  相关角色
                </h2>
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                  {result.characters.map((character) => (
                    <CharacterCard key={character.id} character={character} />
                  ))}
                </div>
              </section>
            )}

            {result.news.length > 0 && (
              <section>
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-newspaper-line text-primary-600" />
                  相关资讯
                </h2>
                <ul className="mt-5 space-y-3">
                  {result.news.map((news) => (
                    <li key={news.id}>
                      <Link
                        to={`/news/${news.id}`}
                        className="group flex flex-col gap-1 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <span className="text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                          {news.title}
                        </span>
                        <span className="shrink-0 text-xs text-foreground-400">
                          {formatDate(news.date)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}